import {
  Divider,
  Grid,
  Typography,
  Box,
  makeStyles,
  Chip,
  Paper,
  CircularProgress,
  Fade,
} from "@material-ui/core";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useFirebase } from "../../../Firebase/FirebaseContext";
import {
  History,
  MeetingRoom,
  EventAvailable,
  ArrowBack,
} from "@material-ui/icons";
import AppointmentCard from "../../Elements/AppointmentCard";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(4),
    animation: "$fadeIn 0.6s ease-out",
  },
  header: {
    marginBottom: theme.spacing(4),
    position: "relative",
    padding: theme.spacing(4),
    background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.light}10 100%)`,
    borderRadius: 20,
    border: `2px solid ${theme.palette.primary.main}22`,
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    },
  },
  title: {
    fontWeight: 800,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    justifyContent: "center",
    fontSize: "2.5rem",
  },
  roomIcon: {
    fontSize: 56,
    color: theme.palette.primary.main,
    animation: "$pulse 2s ease-in-out infinite",
    filter: "drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))",
  },
  subtitle: {
    color: theme.palette.text.secondary,
    textAlign: "center",
    fontSize: "1.1rem",
    fontWeight: 500,
  },
  currentSection: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(4),
    background: `linear-gradient(135deg, ${theme.palette.success.light}22 0%, ${theme.palette.success.main}15 100%)`,
    borderRadius: 20,
    border: `2px solid ${theme.palette.success.main}`,
    boxShadow: `0 8px 24px ${theme.palette.success.main}22`,
    position: "relative",
    overflow: "hidden",
    animation: "$slideInDown 0.5s ease-out",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
    },
  },
  sectionTitle: {
    fontWeight: 700,
    color: theme.palette.success.dark,
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    fontSize: "1.5rem",
  },
  historySection: {
    marginTop: theme.spacing(4),
  },
  historyTitle: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    fontSize: "1.5rem",
    paddingBottom: theme.spacing(2),
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  loadingState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
    gap: theme.spacing(3),
  },
  loadingIcon: {
    color: theme.palette.primary.main,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(10),
    animation: "$fadeIn 0.8s ease-out",
    background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
    borderRadius: 20,
    border: `2px dashed ${theme.palette.divider}`,
  },
  emptyIcon: {
    fontSize: 100,
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(3),
    opacity: 0.3,
  },
  "@keyframes fadeIn": {
    "0%": {
      opacity: 0,
    },
    "100%": {
      opacity: 1,
    },
  },
  "@keyframes pulse": {
    "0%, 100%": {
      transform: "scale(1)",
    },
    "50%": {
      transform: "scale(1.05)",
    },
  },
  "@keyframes slideInDown": {
    "0%": {
      opacity: 0,
      transform: "translateY(-20px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
}));

export default function RoomHistory() {
  const classes = useStyles();
  const location = useLocation();
  const { firebase } = useFirebase();
  const [roomHistory, setRoomHistory] = useState([]);
  const [currentAppointment, setCurrentAppointment] = useState();
  const [loading, setLoading] = useState(true);
  const room = location.state.data;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const roomRef = doc(firebase.db, `rooms/${room.id}`);
        const docData = await getDoc(roomRef);

        if (docData.data()?.appointementId) {
          const currentAppointmentRef = doc(
            firebase.db,
            `appointements/${docData.data().appointementId}`
          );
          const appDoc = await getDoc(currentAppointmentRef);
          // L'identifiant du document doit etre recopie dans l'objet, comme
          // dans l'historique juste en dessous : `appDoc.data()` seul laissait
          // la tuile du rendez-vous en cours sans `id`, et le bouton du resume
          // partait en `pdf/getAppointment/undefined` — 500 en production.
          // Un document absent n'affiche rien : la section reste vide.
          if (appDoc.exists()) {
            setCurrentAppointment({ id: appDoc.id, ...appDoc.data() });
          }
        }

        const appointmentsRef = collection(firebase.db, `appointements`);
        const q = query(
          appointmentsRef,
          where("roomId", "==", room.id),
          orderBy("started_at", "desc"),
          limit(30)
        );
        const qSnapshot = await getDocs(q);

        let temp = [];
        qSnapshot.forEach((appointment) => {
          temp.push({ id: appointment.id, ...appointment.data() });
        });

        setRoomHistory(temp);
        setLoading(false);
      } catch (error) {
        console.error("Erreur lors du chargement:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [firebase.db, room.id]);
  console.log(room);

  if (loading) {
    return (
      <div className={classes.container}>
        <Box className={classes.loadingState}>
          <CircularProgress
            size={60}
            className={classes.loadingIcon}
            thickness={4}
          />
          <Typography
            variant="h5"
            style={{ fontWeight: 600, color: "#6B7280" }}
          >
            Chargement de l'historique...
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Fade in timeout={600}>
        <Box className={classes.header}>
          <Typography variant="h3" className={classes.title}>
            <MeetingRoom className={classes.roomIcon} />
            {room?.room?.name}
          </Typography>
          <Typography variant="body1" className={classes.subtitle}>
            Historique des rendez-vous (30 derniers)
          </Typography>
        </Box>
      </Fade>

      {currentAppointment && (
        <Fade in timeout={800}>
          <Box className={classes.currentSection}>
            <Typography variant="h5" className={classes.sectionTitle}>
              <EventAvailable style={{ fontSize: 32 }} />
              Rendez-vous en cours
            </Typography>
            <AppointmentCard appointment={currentAppointment} />
          </Box>
        </Fade>
      )}

      <Fade in timeout={1000}>
        <Box className={classes.historySection}>
          <Typography variant="h5" className={classes.historyTitle}>
            <History style={{ fontSize: 32 }} />
            Historique
          </Typography>

          {roomHistory.length > 0 ? (
            <Grid container spacing={3}>
              {roomHistory.map((appointment, i) => (
                <AppointmentCard
                  appointment={appointment}
                  key={`appointment-uid-${i}`}
                />
              ))}
            </Grid>
          ) : (
            <Box className={classes.emptyState}>
              <History className={classes.emptyIcon} />
              <Typography
                variant="h4"
                gutterBottom
                style={{ fontWeight: 700, color: "#374151" }}
              >
                Aucun historique
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                style={{ fontSize: "1.1rem" }}
              >
                Cette salle n'a pas encore d'historique de rendez-vous
              </Typography>
            </Box>
          )}
        </Box>
      </Fade>
    </div>
  );
}
